/**
 * Nav-rail width model.
 *
 * The rail has two useful shapes — icons only, and icons with labels — but is
 * a continuous drag, so the range between them is a snap zone: release
 * anywhere under the labelled width and the rail settles to one end or the
 * other rather than sitting at an in-between width where labels would clip.
 * Above the labelled width the drag is free, so a user with long panel names
 * can buy the extra room.
 *
 * Inside the zone the rail paints the end it would settle to, flipping at the
 * midpoint — so what you are looking at mid-drag is what you get on release.
 *
 *   36 ────────── snap zone ────────── 120 ─── free ─── 240
 *   icons only                      labels          labels, roomier
 *
 * All three numbers are BUTTON widths. The rail adds NAV_PAD on its left,
 * top and bottom — but NOT on its right: the stage's own --pad (the same
 * 16px) already sits between the rail and the first panel, so padding both
 * would leave the buttons twice as far from the panel as from the window
 * edge.
 */

export const NAV_MIN = 36; //< Icon-only button (square).
export const NAV_SNAP = 120; //< Narrowest labelled button; the snap zone's upper end.
export const NAV_MAX = 240; //< Widest the rail can be dragged.
export const NAV_PAD = 16; //< Rail padding: left, top and bottom (see above).

// Anything under this releases to NAV_MIN, anything over to NAV_SNAP.
const SNAP_MIDPOINT = (NAV_MIN + NAV_SNAP) / 2;

/** Total rail width for a given button width — one gutter, on the left. */
export function getRailWidth(buttonWidth: number): number {
    return buttonWidth + NAV_PAD;
}

/** Labels are only rendered once the button is wide enough to hold them. */
export function showsLabels(buttonWidth: number): boolean {
    return buttonWidth >= NAV_SNAP;
}

/**
 * Resolve a dragged pixel width to a width the rail may actually rest at.
 * Used on pointer release and when reading a persisted value, so a
 * hand-edited or stale storage entry can't wedge the rail mid-zone.
 */
export function snapNavWidth(px: number): number {
    if (!Number.isFinite(px)) {
        return NAV_SNAP;
    }
    if (px >= NAV_SNAP) {
        return Math.min(px, NAV_MAX);
    }
    return px < SNAP_MIDPOINT ? NAV_MIN : NAV_SNAP;
}

/** Alias used at the storage boundary, where "clamp" reads truer than "snap". */
export const clampNavWidth = snapNavWidth;

/**
 * The width to PAINT while a drag is in flight — the same width the release
 * will keep, which is the whole point: the rail must never show a size the
 * drop will not honour.
 *
 * This used to pin the rail at NAV_MIN across the entire snap zone, on the
 * reasoning that the intermediate widths are not states it can rest in. True,
 * but it made the upper half of the zone lie: drag to 100, watch the rail
 * collapse to icons, let go, and it springs back out to NAV_SNAP because
 * that is the end 100 snaps to. Painting the destination keeps the original
 * intent — only ever NAV_MIN, NAV_SNAP, or a free-zone width, never an
 * in-between — while making the preview honest.
 */
export const previewNavWidth = snapNavWidth;
